import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Eye, Trash, Send, BellRing, Download } from 'lucide-react';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import InvoicePDF from '../Invoice/InvoicePDF';
import EditInvoicePopup from './EditInvoicePopup';
import RecurringInvoicesPopup from './RecurringInvoicesPopup';
import supabase from '../../supabase';
import QrCode from 'qrcode';
import './InvoiceTable.css';
import { useToast } from '../Invoice/Toast';

const InvoiceTable = ({ 
  sectorStatus, 
  selectedInvoiceData, 
  onClearSelection,
  filteredInvoices: propFilteredInvoices,
  clients: propClients,
  isClientView = false,
  statusFilter: propStatusFilter,
  updatingStatus: propUpdatingStatus,
  sendingInvoice: propSendingInvoice,
  sendingReminder: propSendingReminder,
  userProfile: propUserProfile
}) => {
  // Core Data States
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [clients, setClients] = useState({});
  const [banks, setBanks] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  // Filter and Search States
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');

  // Loading States
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [sendingInvoice, setSendingInvoice] = useState({});
  const [sendingReminder, setSendingReminder] = useState({});

  // PDF Preview States
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewInvoiceItems, setPreviewInvoiceItems] = useState([]);
  const [previewInvoiceTerms, setPreviewInvoiceTerms] = useState([]);
  const [previewClient, setPreviewClient] = useState(null);
  const [previewUser, setPreviewUser] = useState(null);
  const [previewBank, setPreviewBank] = useState(null);
  const [previewQrCode, setPreviewQrCode] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pdfViewerKey, setPdfViewerKey] = useState(0);

  // Mobile PDF States
  const [isMobile, setIsMobile] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);

  // Edit States
  const [editInvoice, setEditInvoice] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);

  // Recurring States
  const [showRecurringPopup, setShowRecurringPopup] = useState(false);
  const [selectedRecurringInvoice, setSelectedRecurringInvoice] = useState(null);
  const [recurringChildren, setRecurringChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);

  // Delete Popup State
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // Tax and Preview States
  const [previewTaxType, setPreviewTaxType] = useState('NONE');
  const [previewShowHsnColumn, setPreviewShowHsnColumn] = useState(false);
  const [previewShowValueColumn, setPreviewShowValueColumn] = useState(false);
  const [previewShowTaxPerItemColumn, setPreviewShowTaxPerItemColumn] = useState(false);
  const [previewHasdiscount, setPreviewHasdiscount] = useState(false);
  const [previewDiscount, setPreviewDiscount] = useState(0);
  const [previewSgstRate, setPreviewSgstRate] = useState(9);
  const [previewCgstRate, setPreviewCgstRate] = useState(9);
  const [previewIgstRate, setPreviewIgstRate] = useState(18);
  
  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast();

  const filterOptions = ['All', 'Paid', 'Unpaid', 'Overdue', 'Recurring'];

  // Helper functions for enhanced email functionality
  const numberToWords = (num) => {
    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
    const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' HUNDRED ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    const convertLargeNumber = (n) => {
      if (n === 0) return '';
      let result = '';
      let crores = Math.floor(n / 10000000);
      n %= 10000000;
      let lakhs = Math.floor(n / 100000);
      n %= 100000;
      let thousands = Math.floor(n / 1000);
      n %= 1000;

      if (crores > 0) result += convertHundreds(crores) + 'CRORE ';
      if (lakhs > 0) result += convertHundreds(lakhs) + 'LAKH ';
      if (thousands > 0) result += convertHundreds(thousands) + 'THOUSAND ';
      if (n > 0) result += convertHundreds(n);

      return result;
    };

    const integerPart = Math.floor(num);
    const decimalPart = Math.round((num - integerPart) * 100);
    let result = integerPart === 0 ? 'ZERO' : convertLargeNumber(integerPart).trim();

    result += ' RUPEES';
    if (decimalPart > 0) {
      result += ' AND ' + convertHundreds(decimalPart).trim() + ' PAISE';
    }
    result += ' ONLY';
    return result;
  };

  const getLocalItemTotal = (item) => {
    const qty = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity || 0;
    const price = typeof item.rate_per_item === 'string' ? parseFloat(item.rate_per_item) || 0 : item.rate_per_item || 0;
    return qty * price;
  };

  const calculateLocalItemTax = (item, sgstRate = 0, cgstRate = 0) => {
    const base = getLocalItemTotal(item);
    return base * ((sgstRate + cgstRate) / 100);
  };

  const calculateLocalSummary = (items) => {
    let subtotal = 0, totalTax = 0;
    let sgstTotal = 0, cgstTotal = 0, igstTotal = 0;
    
    items.forEach(item => {
      const base = getLocalItemTotal(item);
      const tax = parseFloat(item.total_tax) || 0;
      subtotal += base;
      totalTax += tax;
      
      if (tax > 0) {
        sgstTotal += tax / 2;
        cgstTotal += tax / 2;
      }
    });

    const total = subtotal + totalTax;
    
    return { 
      subtotal, 
      totalTax, 
      total, 
      sgstTotal, 
      cgstTotal, 
      igstTotal,
      discountAmount: 0 
    };
  };

  const generateUPIQRCode = async (upiId, amount, merchantName, invoiceNumber) => {
    const upiString = `upi://pay?pa=${upiId}&am=${amount}&pn=${encodeURIComponent(merchantName)}&tn=${encodeURIComponent(`Invoice ${invoiceNumber}`)}&cu=INR`;
    return await QrCode.toDataURL(upiString, {
      width: 200,
      margin: 1,
      color: { dark: '#000000', light: '#FFFFFF' },
      errorCorrectionLevel: 'M'
    });
  };

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || 
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // n useEffect for initial data fetch - skip if client view
  useEffect(() => {
    if (!isClientView) {
      fetchAllData();
    } else {
      fetchBasicData();
    }
  }, [isClientView]);

  // Handle props from parent (client view)
  useEffect(() => {
    if (isClientView && propFilteredInvoices) {
      setInvoices(propFilteredInvoices);
      setFilteredInvoices(propFilteredInvoices);
    }
  }, [isClientView, propFilteredInvoices]);

  useEffect(() => {
    if (isClientView && propClients) {
      setClients(propClients);
    }
  }, [isClientView, propClients]);

  useEffect(() => {
    if (propStatusFilter) {
      setStatusFilter(propStatusFilter);
    }
  }, [propStatusFilter]);

  useEffect(() => {
    if (propUpdatingStatus) {
      setUpdatingStatus(propUpdatingStatus);
    }
  }, [propUpdatingStatus]);

  useEffect(() => {
    if (propSendingInvoice) {
      setSendingInvoice(propSendingInvoice);
    }
  }, [propSendingInvoice]);

  useEffect(() => {
    if (propSendingReminder) {
      setSendingReminder(propSendingReminder);
    }
  }, [propSendingReminder]);

  useEffect(() => {
    if (propUserProfile) {
      setUserProfile(propUserProfile);
    }
  }, [propUserProfile]);

  // Handle sector status prop
  useEffect(() => {
    if (sectorStatus) {
      setStatusFilter(sectorStatus);
    }
  }, [sectorStatus]);

  // Handle selected invoice data prop
  useEffect(() => {
    if (selectedInvoiceData && selectedInvoiceData.invoice) {
      handleInvoiceViewClick(selectedInvoiceData.invoice);
      if (onClearSelection) onClearSelection();
    }
  }, [selectedInvoiceData]);

  // Filter invoices - only apply if not in client view (already filtered)
  useEffect(() => {
    if (isClientView) {
      let filtered = [...invoices];
      
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter(inv => 
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (clients[inv.client_id] || '').toLowerCase().includes(q) ||
          (inv.status || 'Unpaid').toLowerCase().includes(q)
        );
      }
      
      if (statusFilter !== 'All') {
        filtered = filtered.filter(inv => {
          const st = inv.status || 'Unpaid';
          if (statusFilter === 'Overdue') {
            const today = new Date();
            const due = new Date(inv.due_date);
            today.setHours(0,0,0,0);
            due.setHours(0,0,0,0);
            return inv.status === 'Unpaid' && due < today;
          }
          if (statusFilter === 'Recurring') {
            return inv.is_recurring === true;
          }
          return st === statusFilter;
        });
      }
      
      setFilteredInvoices(filtered);
    } else {
      let filtered = [...invoices];
      
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter(inv => 
          (inv.invoice_number || '').toLowerCase().includes(q) ||
          (clients[inv.client_id] || '').toLowerCase().includes(q) ||
          (inv.status || 'Unpaid').toLowerCase().includes(q)
        );
      }
      
      if (statusFilter !== 'All') {
        filtered = filtered.filter(inv => {
          const st = inv.status || 'Unpaid';
          if (statusFilter === 'Overdue') {
            const today = new Date();
            const due = new Date(inv.due_date);
            today.setHours(0,0,0,0);
            due.setHours(0,0,0,0);
            return inv.status === 'Unpaid' && due < today;
          }
          if (statusFilter === 'Recurring') {
            return inv.is_recurring === true;
          }
          return st === statusFilter;
        });
      }
      
      setFilteredInvoices(filtered);
    }
  }, [searchQuery, statusFilter, invoices, clients, isClientView]);

  // Force PDFViewer remount when preview data changes
  useEffect(() => {
    if (!loadingPreview && previewInvoice) {
      setPdfViewerKey(k => k + 1);
    }
  }, [previewInvoice, previewInvoiceItems, previewClient, previewUser, previewBank, loadingPreview]);

  // Fetch all required data (for non-client view)
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (!user) return;

      const [
        { data: profile },
        { data: invoiceList },
        { data: itemList },
        { data: clientList },
        { data: bankList }
      ] = await Promise.all([
        supabase.from('users').select('*').eq('id', user.id).single(),
        supabase.from('invoices').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('invoice_items').select('*').eq('user_id', user.id),
        supabase.from('clients').select('*').eq('user_id', user.id),
        supabase.from('banks').select('*').eq('user_id', user.id),
      ]);

      setUserProfile(profile);
      setInvoices(invoiceList || []);
      setInvoiceItems(itemList || []);
      setBanks(bankList || []);
      
      if (clientList) {
        const clientMap = {};
        clientList.forEach(c => clientMap[c.id] = c.client_name);
        setClients(clientMap);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showError('Data Load Error', 'Failed to load invoice data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch basic data (for client view)
  const fetchBasicData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (!user) return;

      const [
        { data: profile },
        { data: itemList },
        { data: bankList }
      ] = await Promise.all([
        supabase.from('users').select('*').eq('id', user.id).single(),
        supabase.from('invoice_items').select('*').eq('user_id', user.id),
        supabase.from('banks').select('*').eq('user_id', user.id),
      ]);

      setUserProfile(profile);
      setInvoiceItems(itemList || []);
      setBanks(bankList || []);
      
    } catch (error) {
      console.error('Error fetching basic data:', error);
      showError('Data Load Error', 'Failed to load basic data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced Status update functionality
  const handleStatusUpdate = async (invoiceId, newStatus) => {
    const invoice = invoices.find(i => i.id === invoiceId) || recurringChildren.find(i => i.id === invoiceId);
    
    if (!invoice?.email_sent) {
      showError('Status Update Failed', 'Cannot change status. Invoice must be sent first.');
      return;
    }

    setUpdatingStatus(prev => ({ ...prev, [invoiceId]: true }));
    
    try {
      const { error: uErr } = await supabase
        .from('invoices')
        .update({ status: newStatus })
        .eq('id', invoiceId)
        .eq('user_id', user.id);
      
      if (uErr) {
        if (uErr.code === '23514') {
          showError('Invalid Status', `Status "${newStatus}" is not allowed.`);
          return;
        }
        throw uErr;
      }

      const invNum = invoice.invoice_number || 'Unnamed Invoice';

      if (newStatus === 'Paid') {
        const { data: existingIncome } = await supabase
          .from('income')
          .select('id')
          .eq('user_id', user.id)
          .eq('name', invNum)
          .limit(1);

        if (!existingIncome || existingIncome.length === 0) {
          const clientName = clients[invoice.client_id] || 'Unknown Client';
          const today = new Date().toISOString().split('T')[0];
          const { error: iErr } = await supabase
            .from('income')
            .insert([{
              user_id: user.id,
              name: invNum,
              description: clientName,
              total_amount: invoice.total_amount || 0,
              income_date: today,
              e_status: 'i',
              client_id: invoice.client_id
            }]);
          if (iErr) {
            console.error('Income insert failed:', iErr);
            showWarning('Partial Success', 'Status updated but failed to record income.');
          } else {
            showSuccess('Status Updated', 'Invoice marked as paid and income recorded.');
          }
        } else {
          showSuccess('Status Updated', 'Invoice marked as paid.');
        }
      } else if (newStatus === 'Unpaid') {
        const { error: dErr } = await supabase
          .from('income')
          .delete()
          .eq('user_id', user.id)
          .eq('name', invNum);
        if (dErr) {
          console.error('Income delete failed:', dErr);
          showWarning('Partial Success', 'Status updated but failed to remove income record.');
        } else {
          showSuccess('Status Updated', 'Invoice marked as unpaid and income removed.');
        }
      }

      setInvoices(prev => prev.map(i =>
        i.id === invoiceId ? { ...i, status: newStatus } : i
      ));
      
      setRecurringChildren(prev => prev.map(i =>
        i.id === invoiceId ? { ...i, status: newStatus } : i
      ));

    } catch (error) {
      console.error('Error updating status:', error);
      showError('Update Failed', 'Error updating invoice status. Please try again.');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  // Enhanced Send invoice email functionality
  const sendInvoiceEmail = useCallback(async (invoice) => {
    if (!invoice) return;
    const clientName = clients[invoice.client_id] || 'Client';
    const invoiceNumber = invoice.invoice_number || 'Invoice';
    setSendingInvoice(prev => ({ ...prev, [invoice.id]: true }));
    
    try {
      const [invoiceResult, itemsResult, clientResult, bankResult, userResult] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoice.id).single(),
        supabase.from('invoice_items').select('*').eq('invoice_id', invoice.id).order('id'),
        supabase.from('clients').select('*').eq('id', invoice.client_id).single(),
        supabase.from('banks').select('*').eq('user_id', user.id),
        supabase.from('users').select('*').eq('id', user.id).single()
      ]);

      const invData = invoiceResult.data;
      const itemsData = itemsResult.data || [];
      const clientData = clientResult.data;
      const bankData = bankResult.data || [];
      const userProfileData = userResult.data;

      if (!clientData?.client_email && !clientData?.email) {
        throw new Error('Client email not found');
      }

      const fromInfo = {
        company_name: invData?.company_name || userProfileData?.company_name || 'Your Company',
        name: invData?.from_name || userProfileData?.full_name || userProfileData?.name || 'Your Name',
        address: invData?.from_address || userProfileData?.address || 'Your Address',
        phone: invData?.from_phone || userProfileData?.phone || '',
        email: invData?.from_email || userProfileData?.email || user?.email || '',
        contact_email: invData?.from_email || userProfileData?.email || user?.email || '',
        gst_number: invData?.business_number || userProfileData?.gst_number || ''
      };

      const selectedClient = {
        client_name: clientData?.client_name || '',
        client_company_name: clientData?.client_company_name || '',
        address: clientData?.address || '',
        email: clientData?.client_email || clientData?.email || '',
        client_email: clientData?.client_email || clientData?.email || '',
        client_gst_number: clientData?.client_gst_number || ''
      };

      const invoiceInfo = {
        number: invData?.invoice_number || '',
        issueDate: invData?.issue_date ? new Date(invData.issue_date).toLocaleDateString() : '',
        dueDate: invData?.due_date ? new Date(invData.due_date).toLocaleDateString() : ''
      };

      const items = itemsData.map(item => ({
        product_name: item?.name || '',
        description: item?.description || '',
        hsn_code: item?.hsn_sac || '',
        hsn: item?.hsn_sac || '',
        quantity: item?.quantity || 0,
        unit_price: item?.rate_per_item || 0,
        rate_per_item: item?.rate_per_item || 0,
        total_tax: item?.total_tax || 0,
        total: item?.total || 0
      }));

      const selectedBank = bankData && bankData.length > 0 ? {
        account_number: bankData[0]?.account_number || '',
        ifsc_code: bankData[0]?.ifsc_code || '',
        bank_name: bankData[0]?.bank_name || '',
        upi_id: bankData[0]?.upi_id || '',
        account_holder_name: bankData[0]?.account_holder_name || fromInfo?.name || fromInfo?.company_name || '',
        branch_name: bankData[0]?.branch || ''
      } : null;

      const localSummary = calculateLocalSummary(items);

      let qrCodeDataURL = null;
      if (selectedBank?.upi_id && localSummary.total < 100001 && localSummary.total > 0) {
        try {
          qrCodeDataURL = await generateUPIQRCode(
            selectedBank.upi_id, 
            localSummary.total.toFixed(2), 
            fromInfo?.company_name || fromInfo?.name || 'Merchant', 
            invoiceInfo.number
          );
        } catch (error) {
          console.error('Error generating QR code:', error);
          qrCodeDataURL = null;
        }
      }

      const invoiceData = {
        fromInfo,
        selectedClient,
        newClient: null,
        invoiceInfo,
        termsAndConditions: invData?.notes ? [invData.notes] : ['Payment due within 30 days'],
        userProfile: {
          ...userProfileData,
          logo_url: userProfileData?.logo_url || userProfile?.logo_url
        },
        selectedBank
      };

      const doc = (
        <InvoicePDF
          invoiceData={invoiceData}
          items={items}
          localSummary={localSummary}
          numberToWords={numberToWords}
          getLocalItemTotal={getLocalItemTotal}
          calculateLocalItemTax={(item) => calculateLocalItemTax(item, 9, 9)}
          showTaxPerItemColumn={localSummary.totalTax > 0}
          showHsnColumn={true}
          hasTax={localSummary.totalTax > 0}
          hasdiscount={false}
          discount={0}
          taxType="SGST_CGST"
          sgstRate={9}
          cgstRate={9}
          igstRate={0}
          selectedBank={selectedBank}
          qrCodeDataURL={qrCodeDataURL}
          showValueColumn={localSummary.totalTax > 0}
        />
      );

      const pdfBlob = await pdf(doc).toBlob();

      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      const res = await fetch(
        'https://dzwtnmlmjwbwqevbhwnv.supabase.co/functions/v1/send-invoice-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from: 'no-reply@bossbyko.com',
            to: clientData.client_email || clientData.email,
            subject: `Invoice ${invoiceNumber}`,
            html: `<p>Hello ${clientName},</p><p>Please find attached invoice <strong>${invoiceNumber}</strong>.</p><p>Best regards,<br>${fromInfo.company_name || fromInfo.name}</p>`,
            attachments: [{
              filename: `invoice-${invoiceNumber}.pdf`,
              content: base64,
              type: 'application/pdf'
            }]
          })
        }
      );

      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to send email');
      
      const { error: updateErr } = await supabase
        .from('invoices')
        .update({ email_sent: true })
        .eq('id', invoice.id)
        .eq('user_id', user.id);
      
      if (updateErr) throw updateErr;
      
      setInvoices(prev => prev.map(i =>
        i.id === invoice.id ? { ...i, email_sent: true } : i
      ));
      
      setRecurringChildren(prev => prev.map(i =>
        i.id === invoice.id ? { ...i, email_sent: true } : i
      ));
      
      showSuccess('Invoice Sent', `Invoice sent successfully to ${clientName}!`);
    } catch (err) {
      console.error('Send invoice error:', err);
      showError('Send Failed', `Failed to send invoice: ${err.message}`);
    } finally {
      setSendingInvoice(prev => ({ ...prev, [invoice.id]: false }));
    }
  }, [user, clients, userProfile, showSuccess, showError]);

  // Enhanced Send reminder functionality
  const sendReminder = useCallback(async (invoice) => {
    if (!invoice) return;
    const clientName = clients[invoice.client_id] || 'Client';
    const invoiceNumber = invoice.invoice_number || 'Invoice';
    setSendingReminder(prev => ({ ...prev, [invoice.id]: true }));
    
    try {
      const { data: clientData, error: cErr } = await supabase
        .from('clients')
        .select('*')
        .eq('id', invoice.client_id)
        .single();
        
      if (cErr || (!clientData.client_email && !clientData.email)) {
        throw new Error('Client email not found');
      }

      const res = await fetch(
        'https://dzwtnmlmjwbwqevbhwnv.supabase.co/functions/v1/send-invoice-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from: 'no-reply@bossbyko.com',
            to: clientData.client_email || clientData.email,
            subject: `Reminder: Invoice ${invoiceNumber} is still unpaid`,
            html: `<p>Hello ${clientName},</p><p>This is a reminder that invoice <strong>${invoiceNumber}</strong> is still marked as unpaid. Please make the payment as soon as possible.</p><p>Thank you for your attention to this matter.</p>`
          })
        }
      );
      
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to send reminder');
      
      showSuccess('Reminder Sent', `Reminder sent successfully to ${clientName}!`);
    } catch (err) {
      console.error('Reminder error:', err);
      showError('Reminder Failed', `Failed to send reminder: ${err.message}`);
    } finally {
      setSendingReminder(prev => ({ ...prev, [invoice.id]: false }));
    }
  }, [clients, showSuccess, showError]);

  // Open delete popup
  const openDeletePopup = (invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeletePopup(true);
  };

  // Close delete popup
  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setInvoiceToDelete(null);
  };

  // Delete invoice functionality
  const deleteInvoice = async (addToIncome = false) => {
    if (!invoiceToDelete) return;
    
    const invoice = invoiceToDelete;
    closeDeletePopup();

    try {
      if (addToIncome) {
        const clientName = clients[invoice.client_id] || 'Unknown Client';
        const today = new Date().toISOString().split('T')[0];

        const { error: incomeErr } = await supabase
          .from('income')
          .insert([
            {
              user_id: user.id,
              name: invoice.invoice_number,
              description: clientName,
              total_amount: invoice.total_amount || 0,
              income_date: today,
              e_status: 'i',
              client_id: invoice.client_id
            }
          ]);

        if (incomeErr) throw incomeErr;
      }

      const { error: deleteErr } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoice.id)
        .eq('user_id', user.id);

      if (deleteErr) throw deleteErr;

      setInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
      setRecurringChildren((prev) => prev.filter((i) => i.id !== invoice.id));
      
      if (addToIncome) {
        showSuccess('Invoice Deleted', 'Invoice deleted and added to income successfully.');
      } else {
        showSuccess('Invoice Deleted', 'Invoice deleted successfully.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showError('Delete Failed', 'Failed to delete invoice. Please try again.');
    }
  };

  // Generate PDF data for invoice
  const generateInvoicePDFData = async (invoice) => {
    const [
      { data: invData },
      { data: itemsData },
      { data: clientData }
    ] = await Promise.all([
      supabase.from('invoices').select('*').eq('id', invoice.id).single(),
      supabase.from('invoice_items').select('*').eq('invoice_id', invoice.id),
      supabase.from('clients').select('*').eq('id', invoice.client_id).single()
    ]);

    const terms = invData?.termsandconditions ? invData.termsandconditions.split('|') : [];
    const columnVisibility = detectColumnVisibility(itemsData || [], invData);
    const selectedBank = banks.length > 0 ? banks[0] : null;
    const summary = calculatePreviewSummary(
      itemsData || [], 
      columnVisibility.detectedTaxType, 
      columnVisibility.sgst, 
      columnVisibility.cgst, 
      columnVisibility.igst,
      columnVisibility.hasDiscount,
      columnVisibility.discountPercent
    );
    const qrCode = await generatePreviewQrCode(selectedBank, summary, invData, userProfile);

    return {
      invoiceData: {
        fromInfo: userProfile,
        selectedClient: clientData,
        invoiceInfo: invData,
        termsAndConditions: terms,
        userProfile: userProfile,
      },
      items: (itemsData || []).map(item => ({
        product_name: item.name || '',
        description: item.description || '',
        hsn_code: item.hsn_sac || '',
        quantity: item.quantity || 0,
        unit_price: item.rate_per_item || 0,
        value: (item.quantity || 0) * (item.rate_per_item || 0),
        tax: calculateItemTax(item, columnVisibility),
        total: (item.quantity || 0) * (item.rate_per_item || 0) + calculateItemTax(item, columnVisibility)
      })),
      localSummary: summary,
      showTaxPerItemColumn: columnVisibility.hasTax,
      showHsnColumn: columnVisibility.hasHsn,
      showValueColumn: true,
      taxType: columnVisibility.detectedTaxType,
      selectedBank: selectedBank,
      qrCodeDataURL: qrCode
    };
  };

  // Calculate item tax
  const calculateItemTax = (item, columnVisibility) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.rate_per_item) || 0;
    const base = qty * price;
    
    if (columnVisibility.detectedTaxType === 'SGST_CGST') {
      return base * ((columnVisibility.sgst + columnVisibility.cgst) / 100);
    } else if (columnVisibility.detectedTaxType === 'IGST') {
      return base * (columnVisibility.igst / 100);
    }
    return 0;
  };

  // Detect column visibility from data
  // Detect column visibility from data
const detectColumnVisibility = (items, invoice) => {
  let hasHsn = false;
  let hasTax = false;
  let detectedTaxType = 'NONE';
  let sgst = 9, cgst = 9, igst = 18;
  let hasDiscount = false;
  let discountPercent = 0;
  
  items.forEach(item => {
    if (item.hsn_sac && item.hsn_sac.trim() !== '') hasHsn = true;
    if ((item.sgst_percent && item.sgst_percent > 0) || (item.cgst_percent && item.cgst_percent > 0)) {
      hasTax = true;
      detectedTaxType = 'SGST_CGST';
      sgst = item.sgst_percent || 9;
      cgst = item.cgst_percent || 9;
    } else if (item.igst_percent && item.igst_percent > 0) {
      hasTax = true;
      detectedTaxType = 'IGST';
      igst = item.igst_percent || 18;
    }
  });
  
  // ✅ FIXED: Read discount as percentage from invoice.discount
  if (invoice?.discount && invoice.discount > 0) {
    hasDiscount = true;
    discountPercent = Number(invoice.discount); // This is already a percentage
  }
  
  return { hasHsn, hasValue: true, hasTax, detectedTaxType, sgst, cgst, igst, hasDiscount, discountPercent };
};

  // Calculate preview summary
  const calculatePreviewSummary = (items, detectedTaxType, sgst, cgst, igst, hasDiscount, discountPercent) => {
  // Step 1: Calculate subtotal (sum of all item base values)
  let subtotal = 0;
  items.forEach(item => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.rate_per_item) || 0;
    subtotal += qty * price;
  });

  // Step 2: Apply discount to get taxable value
  let taxableValue = subtotal;
  let discountAmount = 0;
  
  if (hasDiscount && discountPercent > 0) {
    discountAmount = subtotal * (discountPercent / 100);
    taxableValue = subtotal - discountAmount;
  }

  // Step 3: Calculate tax on taxable value (after discount)
  let sgstTotal = 0, cgstTotal = 0, igstTotal = 0, totalTax = 0;
  
  if (detectedTaxType === 'SGST_CGST') {
    sgstTotal = taxableValue * (sgst / 100);
    cgstTotal = taxableValue * (cgst / 100);
    totalTax = sgstTotal + cgstTotal;
  } else if (detectedTaxType === 'IGST') {
    igstTotal = taxableValue * (igst / 100);
    totalTax = igstTotal;
  }

  // Step 4: Calculate final total
  const finalTotal = taxableValue + totalTax;
  
  return { 
    subtotal,        // Original subtotal before discount
    totalTax,        // Total tax amount
    total: finalTotal,  // Final total = taxable value + tax
    sgstTotal,       // SGST amount
    cgstTotal,       // CGST amount
    igstTotal,       // IGST amount
    discountAmount   // Discount amount
  };
};


  // Generate QR Code
  const generatePreviewQrCode = async (bank, summary, invoiceInfo, userInfo) => {
    if (!bank?.upi_id) return '';
    
    try {
      const { default: QRCode } = await import('qrcode');
      const upiString = `upi://pay?pa=${bank.upi_id}&am=${summary.total.toFixed(2)}&pn=${encodeURIComponent(userInfo?.company_name || userInfo?.name || 'Merchant')}&tn=${encodeURIComponent(`Invoice ${invoiceInfo.invoice_number}`)}&cu=INR`;
      const qrData = await QRCode.toDataURL(upiString, { width: 150 });
      return qrData;
    } catch (err) {
      console.error('Error generating QR code:', err);
      return '';
    }
  };

  // Handle invoice view click
  const handleInvoiceViewClick = async (invoice) => {
    if (invoice.is_recurring && statusFilter === 'Recurring') {
      handleRecurringInstancesClick(invoice);
      return;
    }

    setLoadingPreview(true);
    setPdfBlob(null);
    
    try {
      const [
        invoiceResult,
        itemsResult,
        clientResult,
        userResult,
        bankResult,
      ] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoice.id).single(),
        supabase.from('invoice_items').select('*').eq('invoice_id', invoice.id),
        supabase.from('clients').select('*').eq('id', invoice.client_id).single(),
        supabase.from('users').select('*').eq('id', user.id).single(),
        supabase.from('banks').select('*').eq('user_id', user.id),
      ]);

      if (invoiceResult.error) throw new Error(`Invoice fetch error: ${invoiceResult.error.message}`);
      if (clientResult.error) throw new Error(`Client fetch error: ${clientResult.error.message}`);
      if (userResult.error) throw new Error(`User fetch error: ${userResult.error.message}`);

      const invData = invoiceResult.data;
      const itemsData = itemsResult.data || [];
      const clientData = clientResult.data;
      const userData = userResult.data;
      const bankData = bankResult.data || [];

      if (!invData) throw new Error('Invoice data not found');
      if (!clientData) throw new Error('Client data not found');
      if (!userData) throw new Error('User data not found');

      let terms = [];
      if (invData?.termsandconditions) {
        terms = invData.termsandconditions.split('|').filter(term => term.trim() !== '');
      }
      if (terms.length === 0) {
        terms = ['Payment due within 30 days'];
      }

      const columnVisibility = detectColumnVisibility(itemsData, invData);
      const selectedBank = bankData && bankData.length > 0 ? bankData[0] : null;
      const summary = calculatePreviewSummary(
        itemsData, 
        columnVisibility.detectedTaxType, 
        columnVisibility.sgst, 
        columnVisibility.cgst, 
        columnVisibility.igst,
        columnVisibility.hasDiscount,
        columnVisibility.discountPercent
      );

      let qrCode = '';
      try {
        qrCode = await generatePreviewQrCode(selectedBank, summary, invData, userData);
      } catch (qrError) {
        console.warn('QR code generation failed:', qrError);
        qrCode = '';
      }

      const invoiceData = {
        fromInfo: {
          company_name: userData?.company_name || userData?.name || 'Your Company',
          name: userData?.full_name || userData?.name || 'Your Name',
          address: userData?.address || 'Your Address',
          phone: userData?.phone || '',
          email: userData?.email || user?.email || '',
          contact_email: userData?.email || user?.email || '',
          gst_number: userData?.gst_number || '',
          logo_url: userData?.logo_url || ''
        },
        selectedClient: {
          client_name: clientData?.client_name || 'Unknown Client',
          client_company_name: clientData?.client_company_name || '',
          address: clientData?.address || '',
          email: clientData?.client_email || clientData?.email || '',
          client_email: clientData?.client_email || clientData?.email || '',
          client_gst_number: clientData?.client_gst_number || ''
        },
        newClient: null,
        invoiceInfo: {
          number: invData?.invoice_number || 'INV-0000',
          issueDate: invData?.issue_date || new Date().toISOString().split('T')[0],
          dueDate: invData?.due_date || new Date().toISOString().split('T')[0],
          notes: invData?.notes || '',
          status: invData?.status || 'Unpaid'
        },
        termsAndConditions: terms,
        userProfile: userData
      };

      const preparedItems = itemsData.map((item, index) => {
        const qty = Number(item.quantity) || 0;
        const rate = Number(item.rate_per_item) || 0;
        const value = qty * rate;
        
        let tax = 0;
        if (columnVisibility.detectedTaxType === 'SGST_CGST') {
          tax = value * ((columnVisibility.sgst + columnVisibility.cgst) / 100);
        } else if (columnVisibility.detectedTaxType === 'IGST') {
          tax = value * (columnVisibility.igst / 100);
        }
        
        return {
          product_name: item.name || item.product_name || `Item ${index + 1}`,
          description: item.description || '',
          hsn_code: item.hsn_sac || '',
          hsn: item.hsn_sac || '',
          quantity: qty,
          unit_price: rate,
          rate_per_item: rate,
          value: value,
          tax: tax,
          total: value + tax,
          total_tax: tax
        };
      });

      setPreviewInvoice(invData);
      setPreviewInvoiceItems(itemsData);
      setPreviewInvoiceTerms(terms);
      setPreviewClient(clientData);
      setPreviewUser(userData);
      setPreviewBank(selectedBank);
      setPreviewQrCode(qrCode);
      
      setPreviewTaxType(columnVisibility.detectedTaxType);
      setPreviewShowHsnColumn(columnVisibility.hasHsn);
      setPreviewShowValueColumn(columnVisibility.hasValue);
      setPreviewShowTaxPerItemColumn(columnVisibility.hasTax);
      setPreviewHasdiscount(columnVisibility.hasDiscount);
      setPreviewDiscount(columnVisibility.discountPercent);
      setPreviewSgstRate(columnVisibility.sgst);
      setPreviewCgstRate(columnVisibility.cgst);
      setPreviewIgstRate(columnVisibility.igst);

      if (isMobile) {
        try {
          const pdfDoc = pdf(
            <InvoicePDF
              invoiceData={invoiceData}
              items={preparedItems}
              localSummary={summary}
              numberToWords={numberToWords}
              getLocalItemTotal={(item) => {
                const qty = Number(item.quantity) || 0;
                const price = Number(item.rate_per_item || item.unit_price) || 0;
                return qty * price;
              }}
              calculateLocalItemTax={(item) => {
                const qty = Number(item.quantity) || 0;
                const price = Number(item.rate_per_item || item.unit_price) || 0;
                const base = qty * price;
                if (columnVisibility.detectedTaxType === 'SGST_CGST') {
                  return base * ((columnVisibility.sgst + columnVisibility.cgst) / 100);
                } else if (columnVisibility.detectedTaxType === 'IGST') {
                  return base * (columnVisibility.igst / 100);
                }
                return 0;
              }}
              showTaxPerItemColumn={columnVisibility.hasTax}
              showHsnColumn={columnVisibility.hasHsn}
              showValueColumn={columnVisibility.hasValue}
              hasTax={columnVisibility.detectedTaxType !== 'NONE'}
              hasdiscount={columnVisibility.hasDiscount}
              discount={columnVisibility.discountPercent}
              taxType={columnVisibility.detectedTaxType}
              sgstRate={columnVisibility.sgst}
              cgstRate={columnVisibility.cgst}
              igstRate={columnVisibility.igst}
              selectedBank={selectedBank}
              qrCodeDataURL={qrCode}
            />
          );
          
          const blob = await pdfDoc.toBlob();
          setPdfBlob(blob);
        } catch (pdfError) {
          console.error('Error generating mobile PDF:', pdfError);
          setPdfBlob(null);
        }
      }
      
    } catch (error) {
      console.error('Error loading invoice preview:', error);
      showError('Preview Error', `Failed to load invoice preview: ${error.message}`);
      
      setPreviewInvoice(null);
      setPreviewInvoiceItems([]);
      setPreviewInvoiceTerms([]);
      setPreviewClient(null);
      setPreviewUser(null);
      setPreviewBank(null);
      setPreviewQrCode('');
      setPdfBlob(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle recurring instances click
  const handleRecurringInstancesClick = async (invoice) => {
    setSelectedRecurringInvoice(invoice);
    setShowRecurringPopup(true);
    setLoadingChildren(true);
    
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('parent_invoice_id', invoice.id)
        .order('issue_date', { ascending: true });

      if (error) throw error;
      setRecurringChildren(data || []);
    } catch (error) {
      console.error('Error fetching recurring children:', error);
      showError('Load Failed', 'Error loading recurring invoice instances');
      setRecurringChildren([]);
    } finally {
      setLoadingChildren(false);
    }
  };

  // Handle edit invoice
  const handleEditInvoice = (invoice) => {
    setEditInvoice(invoice);
    setShowEditPopup(true);
  };

  // Handle edit save
  const handleEditSave = () => {
    setShowEditPopup(false);
    setEditInvoice(null);
    if (!isClientView) {
      fetchAllData();
    } else {
      fetchBasicData();
    }
  };

  // Mobile PDF handlers
  const handleMobilePDFView = () => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  const handleMobilePDFDownload = () => {
    if (pdfBlob && previewInvoice) {
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${previewInvoice.invoice_number || 'Unknown'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadgeClass = (invoice) => {
    const st = invoice.status || 'Unpaid';
    if (st === 'Paid') return 'invoice-table-status-paid';
    return 'invoice-table-status-unpaid';
  };

  const isInvoiceOverdue = (invoice) => {
    if (!invoice.due_date || invoice.status === 'Paid') return false;
    const today = new Date();
    const due = new Date(invoice.due_date);
    today.setHours(0,0,0,0);
    due.setHours(0,0,0,0);
    return due < today && (invoice.status === 'Unpaid' || !invoice.status);
  };

 const StatusToggle = ({ status, onChange, disabled, emailSent }) => {
    const displayStatus = !emailSent ? 'Draft' : (status || 'Unpaid');
    const isPaid = emailSent && status === 'Paid';
    
    return (
      <button
        type="button"
        className={`invoice-table-status-toggle ${isPaid ? 'paid' : 'unpaid'} ${disabled || !emailSent ? 'disabled' : ''}`}
        onClick={() => emailSent && !disabled ? onChange(status === 'Paid' ? 'Unpaid' : 'Paid') : null}
        disabled={disabled || !emailSent}
        title={!emailSent ? 'Invoice must be sent before status can be changed' : ''}
      >
        <div className="invoice-table-toggle-knob"></div>
        <span className="invoice-table-status-text">
          {displayStatus}
        </span>
      </button>
    );
  };
  if (loading) {
    return (
      <div className="invoice-table-loading">
        <div className="invoice-table-loading-spinner"></div>
        <p>Loading invoices...</p>
      </div>
    );
  }

  if (filteredInvoices.length === 0) {
    return (
      <div className="invoice-table-container">
        <div className="invoice-table-empty-state">
          <div className="invoice-table-empty-icon">📄</div>
          <h3 className="invoice-table-empty-title">No invoices found</h3>
          <p className="invoice-table-empty-description">
            {isClientView ? 'This client has no invoices yet.' : 'No invoices match your current criteria.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="invoices-table-container">
        {/* Desktop Table View */}
        <div className="invoice-table-table-view">
          <div className="invoice-table-wrapper">
            <table className="invoice-table-table">
              <thead className="invoice-table-header">
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Issue Date</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {filteredInvoices.map(inv => (
                  <tr key={inv.id} className={`invoice-table-row ${inv.is_recurring ? 'invoice-table-recurring-row' : ''}`}>
                    <td className="invoice-table-cell">
                      <span className="invoice-table-cell-content">
                        {inv.invoice_number || 'Unnamed'}
                      </span>
                    </td>
                    <td className="invoice-table-cell">
                      <span className="invoice-table-cell-content">
                        {clients[inv.client_id] || 'Unknown Client'}
                      </span>
                    </td>
                    <td className="invoice-table-cell">
                      <span className="invoice-table-cell-content invoice-table-amount">
                        ₹{parseFloat(inv.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="invoice-table-cell">
                      <span className="invoice-table-cell-content">
                        {formatDate(inv.issue_date)}
                      </span>
                    </td>
                    <td className="invoice-table-cell">
                      <div className="invoice-table-status-container">
                        {inv.is_recurring && statusFilter === 'Recurring' ? (
                          <span className="invoice-table-recurring-status">Recurring</span>
                        ) : (
                          <StatusToggle
                            status={inv.status || 'Unpaid'}
                            onChange={(newStatus) => handleStatusUpdate(inv.id, newStatus)}
                            disabled={updatingStatus[inv.id]}
                            emailSent={inv.email_sent === true}
                          />
                        )}
                      </div>
                    </td>
                    <td className="invoice-table-cell">
                      <span className="invoice-table-cell-content">
                        {formatDate(inv.due_date)}
                      </span>
                    </td>
                    
                    <td className="invoice-table-cell">
                      <div className="invoice-table-actions-container">
                        <button
                          className="invoice-table-action-btn invoice-table-view-btn"
                          onClick={() => handleInvoiceViewClick(inv)}
                          disabled={false}
                          title="View Invoice"
                        >
                          {inv.is_recurring && statusFilter === 'Recurring' ? (
                            <>
                              <Eye size={16} className="invoice-table-icon" />
                              <span className="invoice-table-btn-text">Recurring Instances</span>
                            </>
                          ) : (
                            <>
                              <Eye size={16} className="invoice-table-icon" />
                              <span className="invoice-table-btn-text"></span>
                            </>
                          )}
                        </button>
                        
                        {!inv.email_sent && !(inv.is_recurring && statusFilter === 'Recurring') ? (
                          <>
                            <button 
                              className="invoice-table-action-btn invoice-table-edit-btn" 
                              onClick={() => handleEditInvoice(inv)}
                              title="Edit Invoice"
                            >
                              <Pencil size={16} className="invoice-table-icon" />
                              <span className="invoice-table-btn-text"></span>
                            </button>
                            
                            <button
                              className="invoice-table-action-btn invoice-table-send-btn"
                              onClick={() => sendInvoiceEmail(inv)}
                              disabled={sendingInvoice[inv.id]}
                              title="Send Invoice"
                            >
                              {sendingInvoice[inv.id] ? (
                                <>
                                  <div className="invoice-table-spinner"></div>
                                  <span className="invoice-table-btn-text">Sending</span>
                                </>
                              ) : (
                                <>
                                  <Send size={16} className="invoice-table-icon" />
                                  <span className="invoice-table-btn-text"></span>
                                </>
                              )}
                            </button>
                            
                            <button 
                              className="invoice-table-action-btn invoice-table-delete-btn" 
                              onClick={() => openDeletePopup(inv)}
                              title="Delete Invoice"
                            >
                              <Trash size={16} className="invoice-table-icon" />
                              <span className="invoice-table-btn-text"></span>
                            </button>
                          </>
                        ) : !(inv.is_recurring && statusFilter === 'Recurring') ? (
                          <>
                            {inv.status === 'Unpaid' && (
                              <button
                                className="invoice-table-action-btn invoice-table-reminder-btn"
                                onClick={() => sendReminder(inv)}
                                disabled={sendingReminder[inv.id]}
                                title="Send Reminder"
                              >
                                {sendingReminder[inv.id] ? (
                                  <>
                                    <div className="invoice-table-spinner"></div>
                                    <span className="invoice-table-btn-text">Sending</span>
                                  </>
                                ) : (
                                  <>
                                    <BellRing size={16} className="invoice-table-icon" />
                                    <span className="invoice-table-btn-text"></span>
                                  </>
                                )}
                              </button>
                            )}
                            
                            <button
                              className="invoice-table-action-btn invoice-table-delete-btn"
                              onClick={() => openDeletePopup(inv)}
                              title="Delete Invoice"
                            >
                              <Trash size={16} className="invoice-table-icon" />
                              <span className="invoice-table-btn-text"></span>
                            </button>
                          </>
                        ) : (
                          <button 
                            className="invoice-table-action-btn invoice-table-edit-btn" 
                            onClick={() => handleEditInvoice(inv)}
                            title="Edit Invoice"
                          >
                            <Pencil size={16} className="invoice-table-icon" />
                            <span className="invoice-table-btn-text"></span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

       
        {/* Mobile Card View */}
        <div className="invoice-table-card-view">
          {filteredInvoices.map(inv => (
            <div key={inv.id} className={`invoice-table-card ${inv.is_recurring ? 'invoice-table-recurring-card' : ''}`}>
              <div className="invoice-table-card-header">
                <div className="invoice-table-card-title">
                  <h4>{inv.invoice_number || 'Unnamed'}</h4>
                  <span className="invoice-table-card-amount">
                    ₹{parseFloat(inv.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="invoice-table-card-status">
                  {inv.is_recurring && statusFilter === 'Recurring' ? (
                    <span className="invoice-table-recurring-status">Recurring</span>
                  ) : (
                    <StatusToggle
                      status={inv.status || 'Unpaid'}
                      onChange={(newStatus) => handleStatusUpdate(inv.id, newStatus)}
                      disabled={updatingStatus[inv.id]}
                      emailSent={inv.email_sent === true}
                    />
                  )}
                </div>
              </div>
              
              <div className="invoice-table-card-body">
                <div className="invoice-table-card-field">
                  <span className="invoice-table-card-label">Client:</span>
                  <span className="invoice-table-card-value">{clients[inv.client_id] || 'Unknown Client'}</span>
                </div>
                <div className="invoice-table-card-field">
                  <span className="invoice-table-card-label">Issue Date:</span>
                  <span className="invoice-table-card-value">{formatDate(inv.issue_date)}</span>
                </div>
                <div className="invoice-table-card-field">
                  <span className="invoice-table-card-label">Due Date:</span>
                  <span className="invoice-table-card-value">{formatDate(inv.due_date)}</span>
                </div>
              </div>
              
              <div className="invoice-table-card-actions">
                <button
                  className="invoice-table-action-btn invoice-table-view-btn"
                  onClick={() => handleInvoiceViewClick(inv)}
                  disabled={false}
                  title="View Invoice"
                >
                  <Eye size={16} className="invoice-table-icon" />
                  <span className="invoice-table-btn-text">
                    {inv.is_recurring && statusFilter === 'Recurring' ? 'Instances' : 'View'}
                  </span>
                </button>
                
                {!inv.email_sent && !(inv.is_recurring && statusFilter === 'Recurring') ? (
                  <>
                    <button 
                      className="invoice-table-action-btn invoice-table-edit-btn" 
                      onClick={() => handleEditInvoice(inv)}
                      title="Edit Invoice"
                    >
                      <Pencil size={16} className="invoice-table-icon" />
                      <span className="invoice-table-btn-text">Edit</span>
                    </button>
                    
                    <button
                      className="invoice-table-action-btn invoice-table-send-btn"
                      onClick={() => sendInvoiceEmail(inv)}
                      disabled={sendingInvoice[inv.id]}
                      title="Send Invoice"
                    >
                      {sendingInvoice[inv.id] ? (
                        <>
                          <div className="invoice-table-spinner"></div>
                          <span className="invoice-table-btn-text">Sending</span>
                        </>
                      ) : (
                        <>
                          <Send size={16} className="invoice-table-icon" />
                          <span className="invoice-table-btn-text">Send</span>
                        </>
                      )}
                    </button>
                    
                    <button 
                      className="invoice-table-action-btn invoice-table-delete-btn" 
                      onClick={() => openDeletePopup(inv)}
                      title="Delete Invoice"
                    >
                      <Trash size={16} className="invoice-table-icon" />
                      <span className="invoice-table-btn-text">Delete</span>
                    </button>
                  </>
                ) : !(inv.is_recurring && statusFilter === 'Recurring') ? (
                  <>
                    {inv.status === 'Unpaid' && (
                      <button
                        className="invoice-table-action-btn invoice-table-reminder-btn"
                        onClick={() => sendReminder(inv)}
                        disabled={sendingReminder[inv.id]}
                        title="Send Reminder"
                      >
                        {sendingReminder[inv.id] ? (
                          <>
                            <div className="invoice-table-spinner"></div>
                            <span className="invoice-table-btn-text">Sending</span>
                          </>
                        ) : (
                          <>
                            <BellRing size={16} className="invoice-table-icon" />
                            <span className="invoice-table-btn-text">Reminder</span>
                          </>
                        )}
                      </button>
                    )}
                    
                    <button
                      className="invoice-table-action-btn invoice-table-delete-btn"
                      onClick={() => openDeletePopup(inv)}
                      title="Delete Invoice"
                    >
                      <Trash size={16} className="invoice-table-icon" />
                      <span className="invoice-table-btn-text">Delete</span>
                    </button>
                  </>
                ) : (
                  <button 
                    className="invoice-table-action-btn invoice-table-edit-btn" 
                    onClick={() => handleEditInvoice(inv)}
                    title="Edit Invoice"
                  >
                    <Pencil size={16} className="invoice-table-icon" />
                    <span className="invoice-table-btn-text">Edit</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PDF Preview Popup with Mobile Support */}
      {previewInvoice && (
        <div className="invoice-table-pdf-popup-overlay" onClick={() => setPreviewInvoice(null)}>
          <div className="invoice-table-pdf-popup" onClick={e => e.stopPropagation()}>
            <div className="invoice-table-pdf-popup-header">
              <h3>Invoice Preview - {previewInvoice.invoice_number || 'Unknown'}</h3>
              <div className="invoice-table-pdf-header-actions">
                {isMobile && pdfBlob && (
                  <>
                    <button 
                      className="invoice-table-pdf-action-btn"
                      onClick={handleMobilePDFView}
                    >
                      <Eye size={16} />
                      Open PDF
                    </button>
                    <button 
                      className="invoice-table-pdf-action-btn"
                      onClick={handleMobilePDFDownload}
                    >
                      <Download size={16} />
                      Download
                    </button>
                  </>
                )}
                <button 
                  className="invoice-table-pdf-popup-close" 
                  onClick={() => setPreviewInvoice(null)}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="invoice-table-pdf-content">
              {loadingPreview ? (
                <div className="invoice-table-pdf-loading">
                  <div className="invoice-table-loading-spinner"></div>
                  <p>Loading invoice preview...</p>
                </div>
              ) : isMobile ? (
                <div className="invoice-table-mobile-pdf-message">
                  <div className="invoice-table-mobile-pdf-content">
                    <h4>Invoice PDF Ready</h4>
                    <p>Click "Open PDF" button above to view the invoice in a new tab, or download it directly.</p>
                  </div>
                </div>
              ) : (
                <PDFViewer
                  className="invoice-table-pdf-viewer"
                  width="100%"
                  height="100%"
                  key={`pdf-viewer-${pdfViewerKey}`}
                >
                  <InvoicePDF
                    invoiceData={{
                      fromInfo: previewUser || userProfile || {
                        company_name: '',
                        name: '',
                        address: '',
                        phone: '',
                        contact_email: '',
                        email: '',
                        gst_number: '',
                        logo_url: ''
                      },
                      selectedClient: previewClient,
                      newClient: null,
                      invoiceInfo: {
                        number: previewInvoice.invoice_number || previewInvoice.number || '',
                        issueDate: previewInvoice.issue_date || previewInvoice.issueDate || '',
                        dueDate: previewInvoice.due_date || previewInvoice.dueDate || '',
                        notes: previewInvoice.notes || '',
                        status: previewInvoice.status || 'Unpaid',
                        ...previewInvoice
                      },
                      termsAndConditions: previewInvoiceTerms,
                      userProfile: previewUser || userProfile,
                    }}
                    items={previewInvoiceItems.map((item, index) => {
                      const qty = Number(item.quantity) || 0;
                      const rate = Number(item.rate_per_item) || 0;
                      const value = qty * rate;
                      const tax = (previewTaxType === 'SGST_CGST') ? value * ((previewSgstRate + previewCgstRate) / 100) : 
                                  (previewTaxType === 'IGST') ? value * (previewIgstRate / 100) : 0;
                      
                      return {
                        product_name: item.name || item.product_name || '',
                        description: item.description || '',
                        hsn_code: item.hsn_sac || '',
                        hsn: item.hsn_sac || '',
                        quantity: qty,
                        unit_price: rate,
                        rate_per_item: rate,
                        value: value,
                        tax: tax,
                        total: value + tax
                      };
                    })}
                    localSummary={calculatePreviewSummary(
                      previewInvoiceItems, 
                      previewTaxType, 
                      previewSgstRate, 
                      previewCgstRate, 
                      previewIgstRate,
                      previewHasdiscount,
                      previewDiscount
                    )}
                    numberToWords={numberToWords}
                    getLocalItemTotal={(item) => {
                      const qty = Number(item.quantity) || 0;
                      const price = Number(item.rate_per_item || item.unit_price) || 0;
                      return qty * price;
                    }}
                    calculateLocalItemTax={(item) => {
                      const qty = Number(item.quantity) || 0;
                      const price = Number(item.rate_per_item || item.unit_price) || 0;
                      const base = qty * price;
                      if (previewTaxType === 'SGST_CGST') {
                        return base * ((previewSgstRate + previewCgstRate) / 100);
                      } else if (previewTaxType === 'IGST') {
                        return base * (previewIgstRate / 100);
                      }
                      return 0;
                    }}
                    showTaxPerItemColumn={previewShowTaxPerItemColumn}
                    showHsnColumn={previewShowHsnColumn}
                    showValueColumn={previewShowValueColumn}
                    hasTax={previewTaxType !== 'NONE'}
                    hasdiscount={previewHasdiscount}
                    discount={previewDiscount}
                    taxType={previewTaxType}
                    sgstRate={previewSgstRate}
                    cgstRate={previewCgstRate}
                    igstRate={previewIgstRate}
                    selectedBank={previewBank}
                    qrCodeDataURL={previewQrCode}
                  />
                </PDFViewer>
              )}
            </div>
            
            <div className="invoice-table-pdf-popup-actions">
              <button className="invoice-table-button--popup" onClick={() => setPreviewInvoice(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Popup */}
      <EditInvoicePopup
        invoice={editInvoice}
        isOpen={showEditPopup}
        onClose={() => setShowEditPopup(false)}
        onSave={handleEditSave}
        clients={clients}
      />

      {/* Recurring Instances Popup */}
      <RecurringInvoicesPopup
        show={showRecurringPopup}
        onClose={() => setShowRecurringPopup(false)}
        selectedRecurringInvoice={selectedRecurringInvoice}
        clients={clients}
        recurringChildren={recurringChildren}
        loadingChildren={loadingChildren}
        editingInvoice={null}
        editFormData={{}}
        setEditFormData={() => {}}
        updatingStatus={updatingStatus}
        sendingInvoice={sendingInvoice}
        sendingReminder={sendingReminder}
        isInvoiceOverdue={isInvoiceOverdue}
        onInvoiceClick={handleInvoiceViewClick}
        onStatusUpdate={handleStatusUpdate}
        onSendInvoiceEmail={sendInvoiceEmail}
        onSendReminder={sendReminder}
        onStartEdit={() => {}}
        onSaveEdit={() => {}}
        onCancelEdit={() => {}}
        onDeleteInvoice={deleteInvoice}
        onEditInvoice={handleEditInvoice}
      />
      {/* Delete Invoice Popup */}
      {showDeletePopup && invoiceToDelete && (
        <div className="delete-popup-overlay" onClick={closeDeletePopup}>
          <div className="delete-popup" onClick={e => e.stopPropagation()}>
            <div className="delete-popup-header">
              <div className="delete-popup-icon-wrapper">
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <h3 className="delete-popup-title">Delete Invoice</h3>
            </div>
            
            <div className="delete-popup-content">
              <p className="delete-popup-message">
                Are you sure you want to delete invoice <strong>{invoiceToDelete.invoice_number || 'Unnamed'}</strong>
                {clients[invoiceToDelete.client_id] && ` for ${clients[invoiceToDelete.client_id]}`}?
              </p>
              
              <div className="delete-popup-info">
                <div className="delete-popup-info-item">
                  <span className="delete-popup-label">Amount:</span>
                  <span className="delete-popup-value">
                    ₹{parseFloat(invoiceToDelete.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {invoiceToDelete.status && (
                  <div className="delete-popup-info-item">
                    <span className="delete-popup-label">Status:</span>
                    <span className={`delete-popup-status ${invoiceToDelete.status.toLowerCase()}`}>
                      {invoiceToDelete.status}
                    </span>
                  </div>
                )}
              </div>

              <div className="delete-popup-warning">
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>This action cannot be undone.</span>
              </div>
            </div>
            
            <div className="delete-popup-actions">
              <button 
                className="delete-popup-btn delete-popup-btn-cancel"
                onClick={closeDeletePopup}
              >
                Cancel
              </button>
              
              <button 
                className="delete-popup-btn delete-popup-btn-delete"
                onClick={() => deleteInvoice(false)}
              >
                Delete Only
              </button>
              
              <button 
                className="delete-popup-btn delete-popup-btn-delete-income"
                onClick={() => deleteInvoice(true)}
              >
                Delete & Add to Income
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </>
    
  );
};

export default InvoiceTable;